import { useStorage } from "@platform/hooks";
import {
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  GridState,
  PaginationChangedEvent,
  SortChangedEvent,
  SortModelItem,
} from "ag-grid-community";
import { useCallback, useRef } from "react";

export const DEFAULT_PAGE_SIZE = 10;

/**
 * The slice of a table's session-storage entry this hook owns. Callers are free
 * to keep their own keys (search text, filters, tab) in the same entry.
 */
export type AgGridPaginationState = {
  page?: number;
  rowsPerPage?: number;
};

type UseAgGridOptionsArgs = {
  sessionStorageKey?: string;
  onSortPersistState?: (sortModel: SortModelItem) => void;
  sort?: "asc" | "desc";
  colId?: string;
  page?: number;
  pageSize?: number;
  overrideOptions?: Omit<
    GridOptions,
    | "rowData"
    | "serverSideDatasource"
    | "rowModelType"
    | "onGridReady"
    | "onGridPreDestroyed"
    | "onSortChanged"
  >;
};

type UseAgGridOptionsRes = Omit<
  GridOptions,
  "rowData" | "serverSideDatasource" | "rowModelType"
> & { resetPage: VoidFunction };

export function useAgGridOptions<
  TState extends { sortModel?: SortModelItem[] },
>({
  sessionStorageKey,
  onSortPersistState,
  sort,
  colId,
  overrideOptions = {},
  page,
  pageSize,
}: UseAgGridOptionsArgs): UseAgGridOptionsRes {
  const gridApiRef = useRef<GridApi | null>(null);
  const { getStorageItem, setStorageItem } = useStorage("session");

  // Read the stored page once per mount. Re-reading on later renders would
  // fight the user's own paging, and the value is only ever needed to seed the
  // grid.
  const initialPaginationRef = useRef<Required<AgGridPaginationState> | null>(
    null,
  );
  if (!initialPaginationRef.current) {
    const stored = sessionStorageKey
      ? getStorageItem<AgGridPaginationState>(sessionStorageKey)
      : null;
    initialPaginationRef.current = {
      page: page ?? stored?.page ?? 0,
      rowsPerPage: pageSize ?? stored?.rowsPerPage ?? DEFAULT_PAGE_SIZE,
    };
  }
  const initialPagination = initialPaginationRef.current;

  // AG Grid fires pagination events while it is still settling on its initial
  // page. Persisting those would overwrite the page we are about to restore.
  const isPaginationRestored = useRef(false);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  const onGridPreDestroyed = useCallback(() => {
    gridApiRef.current = null;
  }, []);

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      const { page: restoredPage } = initialPagination;
      if (restoredPage > 0) {
        // A client-side grid knows its full row count here, so a page that no
        // longer exists can be clamped. A server-side grid may still be
        // loading blocks, and its page count would clamp to the wrong value.
        const isServerSide =
          event.api.getGridOption("rowModelType") === "serverSide";
        const lastPage = event.api.paginationGetTotalPages() - 1;
        event.api.paginationGoToPage(
          isServerSide
            ? restoredPage
            : Math.min(restoredPage, Math.max(lastPage, 0)),
        );
      }
      isPaginationRestored.current = true;
    },
    [initialPagination],
  );

  const persistPagination = useCallback(
    (page: number, rowsPerPage: number) => {
      if (!sessionStorageKey) return;
      const currentState = getStorageItem<TState>(sessionStorageKey);
      setStorageItem(sessionStorageKey, {
        ...currentState,
        page,
        rowsPerPage,
      });
    },
    [getStorageItem, sessionStorageKey, setStorageItem],
  );

  const onPaginationChanged = useCallback(
    (event: PaginationChangedEvent) => {
      if (!isPaginationRestored.current) return;
      persistPagination(
        event.api.paginationGetCurrentPage(),
        event.api.paginationGetPageSize(),
      );
    },
    [persistPagination],
  );

  const resetPage = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || api.isDestroyed()) return;
    // Callers reset the page from mount effects as well as from filter
    // changes. Before the grid has rendered its rows there is no page to
    // reset, and clearing the stored one there would throw away the position
    // this hook is about to restore.
    if (!isPaginationRestored.current) return;

    persistPagination(0, api.paginationGetPageSize());
    // A client-side grid re-filters its own rows; only a server-side one has a
    // cache to purge, and asking a client-side grid to purge it is an error.
    if (api.getGridOption("rowModelType") === "serverSide") {
      api.refreshServerSide({ purge: true });
    }
    api.paginationGoToFirstPage();
  }, [persistPagination]);

  const onSortChanged = useCallback(
    (event: SortChangedEvent) => {
      const columnState = event.api
        .getColumnState()
        .filter((x) => x.sortIndex === 0);

      if (!columnState.length) return;
      const sortModel: SortModelItem = {
        sort: columnState[0].sort ?? "asc",
        colId: columnState[0].colId,
      };

      if (sessionStorageKey) {
        const currentState = getStorageItem<TState>(sessionStorageKey);
        setStorageItem(sessionStorageKey, {
          ...currentState,
          sortModel: [sortModel],
        });
      }

      onSortPersistState?.(sortModel);
    },
    [getStorageItem, sessionStorageKey, setStorageItem, onSortPersistState],
  );

  const initialState: GridState = {
    sort: {
      sortModel:
        colId && sort
          ? [
              {
                colId,
                sort,
              },
            ]
          : [],
    },
    pagination: {
      page: initialPagination.page,
      pageSize: initialPagination.rowsPerPage,
    },
  };

  return {
    onGridReady,
    onSortChanged,
    onFirstDataRendered,
    onPaginationChanged,
    resetPage,
    onGridPreDestroyed,
    paginationPageSize: initialPagination.rowsPerPage,
    initialState,
    autoSizeStrategy: { type: "fitGridWidth" },
    tooltipShowDelay: 0,
    paginationPageSizeSelector: [10, 25, 50],
    suppressServerSideFullWidthLoadingRow: true,
    defaultColDef: {
      flex: undefined,
    },
    ...overrideOptions,
  };
}
